import { createContext, useContext, useRef, useState } from "react"
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import {
  PickersActionBar,
  type PickersActionBarProps,
} from "@mui/x-date-pickers/PickersActionBar"
import { ruRU } from "@mui/x-date-pickers/locales"
import type { FieldSelectedSections } from "@mui/x-date-pickers/models"
import dayjs, { type Dayjs } from "dayjs"
import utc from "dayjs/plugin/utc"
import "dayjs/locale/ru"

dayjs.extend(utc)

const displayFormat = "DD.MM.YYYY HH:mm"
const wallMinuteFormat = "YYYY-MM-DDTHH:mm"
const incomplete = "Незавершённая дата или время"

// MUI v6's default parse uses local time, even with timezone="UTC". Parsing
// sections in UTC too avoids browser DST gaps. These carriers are NOT instants.
class WallTimeAdapter extends AdapterDayjs {
  override parse = (value: string, format: string) =>
    value === ""
      ? null
      : dayjs.utc(value, format, true).locale(this.locale ?? "ru")
}

const ActionContext = createContext<Pick<
  PickersActionBarProps,
  "onClear" | "onCancel"
> | null>(null)
function ScheduleActionBar(props: PickersActionBarProps) {
  const actions = useContext(ActionContext)!
  return <PickersActionBar {...props} {...actions} />
}

function wallCarrier(value: string | null | undefined): Dayjs | null {
  if (value == null) return null
  const match =
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/.exec(value)
  if (!match || Number(value.slice(0, 4)) < 1) return dayjs.utc(NaN)
  const normalized = `${match[1]}:${match[2] ?? "00"}.${(match[3] ?? "").padEnd(3, "0")}`
  const carrier = dayjs.utc(`${normalized}Z`)
  // Day.js normally normalizes overflowing days/months instead of rejecting them.
  return carrier.format("YYYY-MM-DDTHH:mm:ss.SSS") === normalized
    ? carrier
    : dayjs.utc(NaN)
}

interface ScheduleDateTimePickerProps {
  label: string
  value: string | null | undefined
  onChange: (value: string | null | undefined) => void
  disabled?: boolean
}

export function ScheduleDateTimePicker({
  label,
  value,
  onChange,
  disabled = false,
}: ScheduleDateTimePickerProps) {
  const carrier = wallCarrier(value)
  const invalid = carrier !== null && !carrier.isValid()
  const [open, setOpen] = useState(false)
  const openingValue = useRef(value)
  const lastValid = useRef(value)
  if (!invalid) lastValid.current = value
  const rawInput = useRef<string | null>(null)
  const selectedSections = useRef<FieldSelectedSections>(null)

  const publish = (next: string | null | undefined) => {
    if (!disabled && next !== value) onChange(next)
  }
  const change = (next: Dayjs | null) => {
    if (disabled) return
    const raw = rawInput.current
    rawInput.current = null
    if (next === null) {
      // MUI v6 reports unparseable whole-field input/paste as null. Only an
      // actually empty input or the explicit clear action may remove a bound.
      publish(raw ? `${incomplete}: ${raw}` : null)
    } else if (!next.isValid()) {
      publish(raw ? `${incomplete}: ${raw}` : incomplete)
    } else {
      const minute = next.format(wallMinuteFormat)
      const previous = lastValid.current
      // No-op input and edits preserve seconds/fractions hidden by the UI.
      publish(
        previous && wallCarrier(previous)?.isValid()
          ? minute + previous.slice(16)
          : `${minute}:00`,
      )
    }
  }
  const captureInput = (text: string) => {
    if (disabled) return
    const raw = text.replace(/\u200e/g, "")
    rawInput.current = raw
    if (raw && !dayjs.utc(raw, displayFormat, true).isValid()) {
      // Also publish when MUI suppresses repeated null/invalid changes. The
      // owner remains the sole validation/wire boundary, so stale valid values
      // cannot be submitted while the field contains unfinished input.
      publish(`${incomplete}: ${raw}`)
    }
  }

  return (
    <ActionContext.Provider
      value={{
        onClear: () => {
          if (disabled) return
          rawInput.current = null
          lastValid.current = null
          publish(null)
          setOpen(false)
        },
        onCancel: () => {
          if (disabled) return
          rawInput.current = null
          publish(openingValue.current)
          setOpen(false)
        },
      }}
    >
      <LocalizationProvider
        dateAdapter={WallTimeAdapter}
        adapterLocale="ru"
        localeText={
          ruRU.components.MuiLocalizationProvider.defaultProps.localeText
        }
      >
        <DateTimePicker
          label={label}
          value={carrier}
          onChange={change}
          onSelectedSectionsChange={(sections) => {
            selectedSections.current = sections
          }}
          timezone="UTC"
          format={displayFormat}
          ampm={false}
          disabled={disabled}
          readOnly={disabled}
          closeOnSelect={false}
          minDate={dayjs.utc("0001-01-01T00:00:00Z")}
          maxDate={dayjs.utc("9999-12-31T23:59:59Z")}
          open={open && !disabled}
          onOpen={() => {
            if (disabled) return
            rawInput.current = null
            openingValue.current = value
            setOpen(true)
          }}
          onClose={() => setOpen(false)}
          slots={{ actionBar: ScheduleActionBar }}
          slotProps={{
            // Keep the portaled popup out of page scroll geometry. Absolute
            // positioning can scroll/flip it between pointer down and up when
            // an action receives focus, losing the click to the page behind it.
            popper: { popperOptions: { strategy: "fixed" } },
            textField: {
              size: "small",
              fullWidth: true,
              error: invalid,
              helperText: invalid
                ? `По Москве · необязательно. Введите ДД.ММ.ГГГГ ЧЧ:ММ. Текущее значение: ${value}`
                : "По Москве · необязательно",
              onChangeCapture: (event) =>
                captureInput((event.target as HTMLInputElement).value),
              onPasteCapture: (event) => {
                const text = event.clipboardData.getData("text")
                const selection = selectedSections.current
                const singleSection =
                  selection !== null &&
                  selection !== "all" &&
                  (typeof selection !== "object" ||
                    selection.startIndex === selection.endIndex)
                // MUI handles numeric section pastes (and ignores letters in
                // these numeric sections). It emits no change for a no-op, so
                // do not first mark those fragments as invalid whole dates.
                if (singleSection && /^(?:\d+|[a-zA-Z]+)$/.test(text)) {
                  rawInput.current = null
                  return
                }
                captureInput(text)
              },
              onKeyDownCapture: () => {
                rawInput.current = null
              },
            },
            actionBar: {
              actions: ["clear", "cancel", "accept"],
            },
          }}
        />
      </LocalizationProvider>
    </ActionContext.Provider>
  )
}
